import React, { useState } from 'react'
import { Hash, X, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'

interface PostTagsEditorProps {
  hashtags: string[]
  onHashtagsChange: (tags: string[]) => void
  platform?: string
  error?: string | null
  isSubmitting: boolean
}

export function PostTagsEditor({
  hashtags,
  onHashtagsChange,
  platform,
  error,
  isSubmitting
}: PostTagsEditorProps) {
  const { error: toastError } = useToast()
  const [newHashtag, setNewHashtag] = useState('')

  const addHashtag = () => {
    if (!newHashtag || newHashtag.trim() === '') return

    const tag = newHashtag.startsWith('#') ? newHashtag : `#${newHashtag}`

    // Validate hashtag format
    const hashtagRegex = /^#[\w]+$/
    if (!hashtagRegex.test(tag)) {
      toastError('Invalid Hashtag', 'Hashtags can only contain letters, numbers, and underscores')
      return
    }

    // Check for duplicates (case-insensitive)
    const isDuplicate = hashtags.some(h => h.toLowerCase() === tag.toLowerCase())
    if (isDuplicate) {
      toastError('Duplicate Hashtag', 'This hashtag already exists')
      return
    }

    // Check platform limits
    if (platform === 'INSTAGRAM' && hashtags.length >= 30) {
      toastError('Hashtag Limit', 'Instagram allows a maximum of 30 hashtags')
      return
    }

    onHashtagsChange([...hashtags, tag])
    setNewHashtag('')
  }

  const removeHashtag = (tagToRemove: string) => {
    onHashtagsChange(hashtags.filter(tag => tag !== tagToRemove))
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium text-muted-foreground">Hashtags</Label>
          <span className="text-xs text-muted-foreground">
            {hashtags.length} / {platform === 'INSTAGRAM' ? '30' :
              platform === 'TWITTER' ? '10' : '30'}
          </span>
        </div>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={newHashtag}
              onChange={(e) => setNewHashtag(e.target.value)}
              placeholder="Add a hashtag..."
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addHashtag())}
              disabled={isSubmitting}
              className={cn(
                "pl-9",
                error && "border-red-500 focus:ring-red-500"
              )}
            />
          </div>
          <Button
            type="button"
            onClick={addHashtag}
            disabled={platform === 'INSTAGRAM' && hashtags.length >= 30}
            variant="secondary"
          >
            Add
          </Button>
        </div>

        {error && (
          <div className="flex items-center gap-1 text-sm text-red-500">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        {hashtags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3 p-3 bg-secondary/30 rounded-lg border border-border/50">
            {hashtags.map((tag) => (
              <Badge key={tag} variant="secondary" className="bg-background hover:bg-background/80 pl-2.5 pr-1 py-1 h-7 text-sm border shadow-sm">
                <span className="mr-1 text-primary">{tag}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0 rounded-full hover:bg-muted"
                  onClick={() => removeHashtag(tag)}
                >
                  <X className="h-3 w-3 text-muted-foreground" />
                </Button>
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

